import type { ReactNode } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@components/ui/chart';
import type { AnalyticsMetrics } from './analyticsTypes';
import { formatCount, formatDateTime, formatMoney, formatRate } from './analyticsFormat';
import { formatDateRange } from './reportLabel';
import { SectionHeading } from './SectionHeading';

interface CurvePoint {
  label: string;
  rate: number;
  n: number;
}

function isCurvePoint(value: unknown): value is CurvePoint {
  return (
    typeof value === 'object' &&
    value !== null &&
    'label' in value &&
    'rate' in value &&
    'n' in value
  );
}

function RateBar({ value }: { value: number }) {
  const bounded = Math.max(0, Math.min(1, value));
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
      <div className="h-full rounded-full bg-primary" style={{ width: `${bounded * 100}%` }} />
    </div>
  );
}

function Stat({
  label,
  value,
  unit,
  children,
}: {
  label: string;
  value: string;
  unit?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl">
          {value}
        </span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </p>
      {children}
    </div>
  );
}

function ReturnCurve({ metrics }: { metrics: AnalyticsMetrics }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const config = {
    rate: { label: t('analytics.health.retentionRate'), color: 'var(--primary)' },
  } satisfies ChartConfig;
  const data: CurvePoint[] = metrics.retention.survival_curve.map((point) => ({
    label: t('analytics.metrics.days', { count: point.checkpoint_days }),
    rate: point.rate,
    n: point.n,
  }));

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <p className="text-xs font-medium text-muted-foreground">
          {t('analytics.health.retention')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('analytics.health.retentionDescription', {
            count: metrics.retention.active_core_customers,
            n: formatCount(metrics.retention.active_core_customers, locale),
          })}
        </p>
      </div>
      <ChartContainer config={config} className="aspect-auto h-48 w-full">
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="analytics-return-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-rate)" stopOpacity={0.25} />
              <stop offset="100%" stopColor="var(--color-rate)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
          <YAxis
            domain={[0, 1]}
            ticks={[0, 0.25, 0.5, 0.75, 1]}
            tickFormatter={(value: number) => formatRate(value, locale)}
            tickLine={false}
            axisLine={false}
            width={44}
          />
          <ChartTooltip
            cursor={false}
            content={({ active, payload }) => {
              const point: unknown = payload?.[0]?.payload;
              if (!active || !isCurvePoint(point)) {
                return null;
              }
              return (
                <div className="grid gap-0.5 rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-xl">
                  <span className="font-medium">{point.label}</span>
                  <span className="tabular-nums">
                    {t('analytics.health.retentionRate')} {formatRate(point.rate, locale)}
                  </span>
                  <span className="text-muted-foreground tabular-nums">
                    {t('analytics.health.retentionSample', { n: formatCount(point.n, locale) })}
                  </span>
                </div>
              );
            }}
          />
          <Area
            dataKey="rate"
            type="monotone"
            stroke="var(--color-rate)"
            strokeWidth={2}
            fill="url(#analytics-return-fill)"
            dot={{ r: 3, fill: 'var(--color-rate)', strokeWidth: 0 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
}

/**
 * Whole-customer-base metrics as of report generation ("截至生成时快照").
 * They do not follow the report period, so they live apart from it.
 */
export function CustomerHealth({
  metrics,
  generatedAt,
}: {
  metrics: AnalyticsMetrics;
  generatedAt: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { activation, matching, alerts } = metrics;

  return (
    <section className="flex flex-col gap-5" aria-labelledby="analytics-health-title">
      <SectionHeading
        id="analytics-health-title"
        title={t('analytics.health.title')}
        description={t('analytics.health.asOf', { time: formatDateTime(generatedAt, locale) })}
      />
      <div className="grid gap-6 sm:grid-cols-3">
        <Stat
          label={t('analytics.health.activation')}
          value={formatRate(activation.attainment_rate, locale)}
        >
          <RateBar value={activation.attainment_rate} />
          <p className="text-xs text-muted-foreground">
            {t('analytics.health.activationDetail', {
              attained: formatCount(activation.attained_customers, locale),
              total: formatCount(activation.cohort_customers, locale),
            })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('analytics.health.activationCohort', {
              range: formatDateRange(activation.cohort_from, activation.cohort_to, locale),
            })}
          </p>
        </Stat>
        <Stat
          label={t('analytics.health.coverage')}
          value={formatRate(matching.coverage_rate, locale)}
        >
          <RateBar value={matching.coverage_rate} />
          <p className="text-xs text-muted-foreground">
            {t('analytics.health.coverageDetail', {
              covered: formatCount(matching.covered_customers, locale),
              active: formatCount(matching.active_customers, locale),
              dishes: formatCount(matching.offered_dishes, locale),
            })}
          </p>
        </Stat>
        <Stat
          label={t('analytics.health.watchlist')}
          value={formatCount(alerts.watchlist_size, locale)}
          unit={t('analytics.health.watchlistCustomers')}
        >
          <dl className="flex items-baseline justify-between gap-2 border-t pt-2 text-xs">
            <dt className="text-muted-foreground">{t('analytics.health.watchlistSpend')}</dt>
            <dd
              className="font-medium tabular-nums"
              title={formatMoney(alerts.historical_contribution_amount, locale)}
            >
              {formatMoney(alerts.historical_contribution_amount, locale, { compact: true })}
            </dd>
          </dl>
          <p className="text-xs text-muted-foreground">
            {t('analytics.health.watchlistSpendHint')}
          </p>
        </Stat>
      </div>
      <ReturnCurve metrics={metrics} />
    </section>
  );
}
